import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Button, Heading, Skeleton } from '@wit-support/ui';
import { ChannelBadge } from './channel-badge.js';
import { StatusPill } from './status-pill.js';
import { MessageBubble } from './message-bubble.js';
import { formatInZone, useAdminTimezone } from './lib/time.js';
import { capitalize } from './lib/capitalize.js';
import { cn } from './lib/cn.js';
const ConversationThreadContext = React.createContext(null);
function useConversationThreadContext(component) {
    const ctx = React.useContext(ConversationThreadContext);
    if (!ctx) {
        throw new Error(`<${component}> must be rendered inside <ConversationThread>`);
    }
    return ctx;
}
const ConversationThreadRoot = React.forwardRef(({ conversationId, conversation, messages, isLoading = false, hasMore = false, onLoadMore, className, children, ...rest }, ref) => {
    const value = React.useMemo(() => ({
        conversationId,
        conversation: conversation ?? null,
        messages,
        isLoading,
        hasMore,
        onLoadMore,
    }), [conversationId, conversation, messages, isLoading, hasMore, onLoadMore]);
    return (_jsx(ConversationThreadContext.Provider, { value: value, children: _jsx("div", { ref: ref, className: cn('flex h-full flex-col bg-[var(--color-surface-canvas)]', className), ...rest, children: children }) }));
});
ConversationThreadRoot.displayName = 'ConversationThread';
const ConversationThreadHeader = React.forwardRef(({ className, ...rest }, ref) => {
    const { conversation } = useConversationThreadContext('ConversationThread.Header');
    if (!conversation) {
        return (_jsx("header", { ref: ref, className: cn('sticky top-0 z-10 flex items-center gap-3 border-b border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] px-4 py-3', className), ...rest, children: _jsx(Skeleton, { className: "h-5 w-48" }) }));
    }
    const title = conversation.subject ||
        `${capitalize(conversation.channelKind)}${conversation.contact?.displayName
            ? ` · ${conversation.contact.displayName}`
            : conversation.contact?.primaryEmail
                ? ` · ${conversation.contact.primaryEmail}`
                : ''}`;
    return (_jsxs("header", { ref: ref, className: cn('sticky top-0 z-10 flex items-center gap-3 border-b border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] px-4 py-3', className), ...rest, children: [_jsx(Heading, { level: 2, size: "xs", className: "min-w-0 flex-1 truncate", children: title }), _jsx(ChannelBadge, { kind: conversation.channelKind, size: "sm" }), _jsx(StatusPill, { kind: "conversation", status: conversation.status, size: "sm" })] }));
});
ConversationThreadHeader.displayName = 'ConversationThread.Header';
function dayKey(iso, zone) {
    return formatInZone(iso, 'yyyy-MM-dd', zone);
}
function dayLabel(iso, zone) {
    return formatInZone(iso, 'EEEE, d MMMM yyyy', zone);
}
export const MessageList = React.forwardRef(({ messages, isLoading = false, hasMore = false, onLoadMore, renderMessage, leading, className, ...rest }, ref) => {
    const tz = useAdminTimezone();
    if (isLoading && messages.length === 0) {
        return (_jsx("div", { ref: ref, className: cn('flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4', className), ...rest, children: [0, 1, 2].map((i) => (_jsx(Skeleton, { className: "h-20 w-full max-w-[680px] rounded-[var(--radius-3)]" }, i))) }));
    }
    if (messages.length === 0) {
        return (_jsx("div", { ref: ref, className: cn('flex flex-1 items-center justify-center px-4 py-8 text-sm text-[var(--color-text-muted)]', className), ...rest, children: "No messages in this conversation" }));
    }
    const items = [];
    let lastDay = null;
    for (const m of messages) {
        const key = dayKey(m.createdAt, tz);
        if (key !== lastDay) {
            items.push(_jsxs("div", { className: "flex items-center gap-3 py-2", role: "separator", children: [_jsx("span", { className: "h-px flex-1 bg-[var(--color-border-subtle)]" }), _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: dayLabel(m.createdAt, tz) }), _jsx("span", { className: "h-px flex-1 bg-[var(--color-border-subtle)]" })] }, `sep-${key}-${m.id}`));
            lastDay = key;
        }
        items.push(_jsx(React.Fragment, { children: renderMessage ? renderMessage(m) : _jsx(MessageBubble, { message: m }) }, m.id));
    }
    return (_jsxs("div", { ref: ref, className: cn('flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4', className), ...rest, children: [leading, hasMore ? (_jsx("div", { className: "flex justify-center", children: _jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: onLoadMore, disabled: isLoading, children: "Load older messages" }) })) : null, items] }));
});
MessageList.displayName = 'MessageList';
const ConversationThreadMessages = React.forwardRef((props, ref) => {
    const { messages, isLoading, hasMore, onLoadMore } = useConversationThreadContext('ConversationThread.Messages');
    return (_jsx(MessageList, { ref: ref, messages: messages, isLoading: isLoading, hasMore: hasMore, onLoadMore: onLoadMore, ...props }));
});
ConversationThreadMessages.displayName = 'ConversationThread.Messages';
export const ConversationThread = Object.assign(ConversationThreadRoot, {
    Header: ConversationThreadHeader,
    Messages: ConversationThreadMessages,
});
//# sourceMappingURL=conversation-thread.js.map